import { FlagIcon } from "react-flag-kit";
import * as moment from "moment-timezone";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IoPeopleCircle } from "react-icons/io5";
import { FaChalkboardUser } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";

import {
  loadUserStorage,
  postCancelClass,
  getSequentialClasses,
  handleCalendarInitialParams,
} from "../../store/middleware";
import CancelDialog from "./CancelDialog";
import { getLocalTimeFromUtc, isDevs } from "../../utils/utils";
import { decryptIfEncrypted } from "../../utils/fieldDecryptor";
import ModalSchedule from "../../components/ScheduleModals/ModalSchedule";
import { endLoading, initLoading } from "../../store/slices/generics.slice";

function ListCalendarSchedule({ schedule }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.userReducer);
  const { nextClasses } = useSelector((state) => state.genericReducer);

  const [openSchedule, setOpenSchedule] = useState(false);
  const [cancelAlertOpen, setCancelAlertOpen] = useState(false);
  const [recurrentSchedule, setRecurrentSchedule] = useState(false);
  const [recurrentScheduleId, setRecurrentScheduleId] = useState("");
  const [recurrentScheduleEndDate, setRecurrentScheduleEndDate] = useState("");
  const [sequentialScheduleCancelClasses, setSequentialScheduleCancelClasses] = useState([]);

  const closeCancelAlert = async () => {
    setCancelAlertOpen(false);
  };

  const cancelSubmit = async (message, cancelledBy, recurrentSchedule, cancelAllRecurrences) => {
    closeCancelAlert();
    await dispatch(initLoading());

    const atualDate = moment();
    let cancelObj = getCancelObj(schedule, message, cancelledBy, user?.role, recurrentSchedule, cancelAllRecurrences);

    await dispatch(postCancelClass(cancelObj));
    await dispatch(handleCalendarInitialParams("list", atualDate, atualDate, true));
    await dispatch(loadUserStorage(false, false, "ListCalendarSchedule"));
    await dispatch(endLoading());
  };

  const getCancelObj = (schedule, message, cancelledBy, role, recurrentSchedule, cancelAllRecurrences) => {
    if (role === "teacher") {
      return getTeacherCancelObj(schedule, message, cancelAllRecurrences);
    } else {
      return getOthersCancelObj(schedule, recurrentSchedule, message, cancelledBy, cancelAllRecurrences);
    }
  };

  const getTeacherCancelObj = (schedule, message = "Cancel by User") => {
    const isFixed = schedule.allocationSchedule === "fixed";
    const cancelObj = { reason: message };
    if (isFixed) {
      cancelObj.allocationScheduleId = schedule.allocationScheduleId;
      cancelObj.reschedule = true;
    } else {
      cancelObj.sequentialScheduleId = schedule.sequentialScheduleId;
    }
    return cancelObj;
  };

  const getOthersCancelObj = (
    schedule,
    recurrentSchedule,
    message = "Cancel by User",
    cancelledBy,
    cancelAllRecurrences,
  ) => {
    const cancelObj = { reason: message, cancelledBy };
    if (recurrentSchedule && cancelAllRecurrences) {
      cancelObj.recurrentScheduleId = schedule.recurrentScheduleId;
    } else {
      cancelObj.sequentialScheduleId = schedule.sequentialScheduleId;
    }
    return cancelObj;
  };

  const ConfirmedCardItem = () => {
    const isStudent = user?.role === "student";
    const isCanceled = schedule.status === "canceled";
    const isPending = schedule.status === "pending";
    const isAccepted = schedule.status === "accepted";
    const hasTeacher = !!schedule.teacher;
    const isDone = schedule.status === "done";

    if (!isStudent && !hasTeacher) {
      return <h4 className={"scheduleWithoutTeacher"}>{t("WITHOUT_TEACHER")}</h4>;
    } else if ((isStudent && !isCanceled) || (isPending && hasTeacher) || isAccepted) {
      return (
        <h4 className={"scheduleConfirmed"}>
          {t("CARD_CLASS_CONFIRMED")}
          <i className="fa fa-check-circle-o"></i>
        </h4>
      );
    } else if (isDone) {
      return (
        <h4 className={"scheduleConfirmed"}>
          {t("CARD_CLASS_DONE")}
          <i className="fa fa-check-circle-o"></i>
        </h4>
      );
    } else {
      return <h4 className={"scheduleConfirmed"} />;
    }
  };

  const EditCardItem = () => {
    if (schedule?.status && schedule?.scheduledEndDateTime && schedule?.scheduledStartDateTime) {
      const isPending = schedule?.status === "pending";
      const isAccepted = schedule?.status === "accepted";

      const localTime = getLocalTimeFromUtc(user);
      let canCancel = true;
      canCancel = moment(localTime).isBefore(schedule?.scheduledStartDateTime);

      if (canCancel) {
        if (isPending || isAccepted) {
          return (
            <button className="scheduleButtonEdit" onClick={() => setOpenSchedule(true)}>
              {t("CLASS_DETAILS_EDIT_CLASS")}
              <i className="fa fa-pencil"></i>
            </button>
          );
        } else {
          return <div className="scheduleCanceled" />;
        }
      } else {
        return <></>;
      }
    } else {
      return <></>;
    }
  };

  const CancelCardItem = () => {
    const isCanceled = schedule.status === "canceled";
    const iscanceledFree = schedule.status === "canceledFree";
    const isCanceledByLingo = schedule.status === "canceledLingo";
    const iscanceledFreeLingo = schedule.status === "canceledFreeLingo";
    const isNoShow = schedule.status === "noShow";
    const isPending = schedule.status === "pending";
    const isAccepted = schedule.status === "accepted";

    if (isCanceled) {
      return <h4 className={"scheduleCanceledByOutLimit"}>{t("CANCELLED_OUT_lIMIT")}</h4>;
    } else if (isCanceledByLingo || iscanceledFreeLingo) {
      return <h4 className={"scheduleCanceled"}>{t("CANCELLED_LINGO")}</h4>;
    } else if (iscanceledFree) {
      return <h4 className={"scheduleCanceled"}>{t("CLASS_CANCELED")}</h4>;
    } else if (isNoShow) {
      return <h4 className={"scheduleIsNoShow"}>{t("NOSHOW")}</h4>;
    } else if (isPending || isAccepted) {
      if (schedule.type === "demo") {
        return <div className="scheduleCanceled" />;
      }
      return (
        <button className="cancelSchedule" onClick={setCancelModalValues}>
          {t("BTN_CANCEL")} <i className="fa fa-times-circle-o"></i>
        </button>
      );
    } else {
      return <div className="scheduleCanceled" />;
    }
  };

  const ClassToolCardItem = () => {
    const isLink =
      (schedule.student && schedule.student.classToolId && schedule.student.classToolId.indexOf("http") > -1) ||
      (schedule.container && schedule.container.classToolId && schedule.container.classToolId.indexOf("http") > -1);

    const classTool =
      schedule.student && schedule.student.classTool
        ? schedule.student.classTool
        : schedule.container && schedule.container.classTool
        ? schedule.container.classTool
        : null;

    const classToolId =
      schedule.student && schedule.student.classToolId
        ? schedule.student.classToolId
        : schedule.container && schedule.container.classToolId
        ? schedule.container.classToolId
        : null;
    return classTool && classToolId ? (
      isLink ? (
        <>
          <button type="submit" className="buttonSave pointer" onClick={() => window.open(classToolId)}>
            {classTool}
          </button>
          {schedule?.student?.isTraining && user?.role !== "student" && user?.role !== "teacher" && (
            <FaChalkboardUser size={25} color={"#004fff"} style={{ paddingLeft: 10 }} />
          )}
          {schedule?.lingoLanguage?.flag === "CS-US-IN" && user?.role !== "student" && (
            <IoPeopleCircle size={25} color={"#004fff"} style={{ paddingLeft: 10 }} />
          )}
        </>
      ) : (
        <>
          <div>
            <span className="buttonSave">{classTool}</span>{" "}
            <span style={{ color: "#5A6C96", fontSize: "11px", fontWeight: "500" }}>{classToolId}</span>
          </div>
          {schedule?.student?.isTraining && user?.role !== "student" && user?.role !== "teacher" && (
            <FaChalkboardUser size={20} color={"#004fff"} style={{ paddingLeft: 10 }} />
          )}
          {schedule?.lingoLanguage?.flag === "CS-US-IN" && user?.role !== "student" && (
            <IoPeopleCircle size={20} color={"#004fff"} style={{ paddingLeft: 10 }} />
          )}
        </>
      )
    ) : (
      ""
    );
  };

  const setCancelModalValues = () => {
    const recurrentSchedule =
      user?.role === "teacher" || user?.role === "companyManager" ? true : schedule.recurrentSchedule;
    setCancelAlertOpen(true);
    setRecurrentSchedule(recurrentSchedule);
    getSequentialSchedules(recurrentSchedule, schedule);
  };

  const getSequentialSchedules = (recurrentSchedule, schedule) => {
    schedule && handleGetSequentialClasses(schedule, recurrentSchedule);
  };

  const handleGetSequentialClasses = (schedule, recurrentSchedule) => {
    const pageNumber = 1;
    const pageSize = 100;
    const params = {
      pageNumber,
      pageSize,
      recurrentScheduleId: schedule?.recurrentScheduleId,
      sequentialScheduleId: schedule?.sequentialScheduleId,
    };
    dispatch(getSequentialClasses(params, recurrentSchedule))
      .then((res) => {
        res &&
          setSequentialScheduleCancelClasses(
            res.filter(
              (sequential) =>
                moment(sequential.scheduledDate).format("YYYY-MM-DD") ===
                moment(schedule.scheduledDate).format("YYYY-MM-DD"),
            ),
          );

        setRecurrentScheduleEndDate(res[res.length - 1]?.scheduledDate);
        setRecurrentScheduleId(res[0]?.recurrentScheduleId);
      })
      .catch((err) => {
        console.log("ERROR ON GET SEQUENTIAL END DATE: ", err);
      });
  };

  const handleCloseSchedule = async () => {
    await dispatch(loadUserStorage(true, false, "ListCalendarSchedule"));
    setOpenSchedule(false);
  };

  const hasTeacher = !!schedule.teacher;
  const isStudent = user?.role === "student";
  const isTeacher = user?.role === "teacher";
  const isManager = user?.role === "companyManager" || user?.role === "customerService" || user?.role === "coordinator";
  const isFirstClass =
    schedule &&
    schedule.student &&
    schedule.student.studentLevelGrades &&
    schedule.student.studentLevelGrades.length === 0 &&
    schedule.type !== "trial"
      ? true
      : false;

  return (
    <div className="listScheduleCard">
      <div className="infosAulas">
        <h3 className="scheduleDateTime">{schedule.timeTotal}</h3>
        <ClassToolCardItem />
        {user?.role !== "teacher" && schedule.extraclasses > 0 && (
          <h3 className="scheduleDateTime extraClassLabel">
            {schedule.extraclasses}{" "}
            {schedule.extraclasses === 1 ? t("EXTRA_CLASS_USED_SINGLE") : t("EXTRA_CLASS_USED_PLURAL")}
          </h3>
        )}
      </div>

      <div className="scheduleDetails">
        <div className="scheduleDetailsInfo">
          <div className="scheduleLanguage">
            <h4 className="scheduleLanguageName">
              <FlagIcon code={schedule.lingoLanguage.flag} />
              {t(decryptIfEncrypted(schedule.lingoLanguage.language.name).toUpperCase())}
            </h4>
            {isManager && (
              <h4 className="scheduleLanguageLevels">
                {schedule.student &&
                  schedule.student.studentLevelGrades &&
                  schedule.student.studentLevelGrades.map((scheduleLevel) => (
                    <Fragment key={JSON.stringify(scheduleLevel)}>
                      {`${t("CARD_PLAN_LEVEL")}: `}
                      {scheduleLevel.level.level}
                    </Fragment>
                  ))}
                {schedule.container && (
                  <>
                    {`${t("CARD_PLAN_LEVEL")}: `}
                    {schedule.container.levelByLingo
                      ? schedule.container.levelByLingo
                      : schedule.container.levelByStudent}
                  </>
                )}
              </h4>
            )}
          </div>

          <div className="scheduleLanguageDetails">
            {schedule?.classScheduleDetails?.length > 0 && (
              <h4 className="scheduleLanguageContent">
                {t("CARD_CLASS_CONTENT")}:{" "}
                <span
                  className={
                    schedule.classScheduleDetails[0].focus.toLowerCase() === "regular" ? "regular" : "specific"
                  }
                >
                  {schedule.classScheduleDetails[0].focus.toLowerCase() !== "demo class"
                    ? t(
                        schedule.classScheduleDetails[0].focus.toLowerCase() === "regular"
                          ? "BTN_REGULAR"
                          : "BTN_SPECIFIC",
                      )
                    : t("DEMO_CLASS")}
                </span>
                {isFirstClass && ` • ${t("FIRST_CLASS")}`}
                {schedule.type === "trial" && ` • ${t("TRIAL_CLASS")}`}
              </h4>
            )}
            {!isTeacher &&
              schedule.teacherLanguageType &&
              schedule.student?.studentLevelGrades[0]?.level?.level !== "A0" &&
              schedule.student?.studentLevelGrades[0]?.level?.level !== "A1" && (
                <h4 style={{ textTransform: "capitalize", paddingLeft: 10, color: "#004fff" }}>
                  {t(schedule.teacherLanguageType.toUpperCase())}
                </h4>
              )}
            {!isStudent &&
              schedule.studentPlan &&
              schedule.studentPlan.plan.trial &&
              schedule.studentPlan.studentPlanLanguages.length > 0 && (
                <h4 className="scheduleLanguageFocus">
                  {`${t("LEVEL")}: ${t(schedule.studentPlan.studentPlanLanguages[0].studentLanguageLevel)}`}
                </h4>
              )}
          </div>
          <div className="scheduleLanguagePeoples">
            {(isManager || (isTeacher && hasTeacher)) && (
              <h4 className="scheduleLanguagePeople">
                {`${t("CARD_CLASS_STUDENT")}: `}
                {schedule.student ? decryptIfEncrypted(schedule.student.user.name) : ""}
                {schedule.container && schedule.container.studentName ? schedule.container.studentName : ""}
              </h4>
            )}
            {isManager && !!schedule.teacher && (
              <h4 className="scheduleLanguagePeople">
                {`${t("CARD_CLASS_TEACHER")}: `}
                {decryptIfEncrypted(schedule.teacher.user.name)}
              </h4>
            )}
          </div>
          {isTeacher && hasTeacher && (
            <h4 className="scheduleLanguageLevels">
              {t("CARD_PLAN_LEVEL")}:
              {schedule.student &&
                schedule.student.studentLevelGrades &&
                schedule.student.studentLevelGrades.map((scheduleLevel) => (
                  <Fragment key={JSON.stringify(scheduleLevel)}>{scheduleLevel.level.level}</Fragment>
                ))}
              {schedule.container
                ? schedule.container.levelByLingo
                  ? schedule.container.levelByLingo
                  : schedule.container.levelByStudent
                : ""}
            </h4>
          )}
        </div>
        <div className="scheduleActions">
          <ConfirmedCardItem />
          {user?.role === "student" && <EditCardItem />}
          <CancelCardItem />

          {user?.role !== "student" && (
            <button className="viewSchedule" onClick={() => navigate(`/class-details/${schedule?.id}`)}>
              {t("BTN_VIEW")} <i className="fa fa-angle-right"></i>
            </button>
          )}
        </div>
      </div>

      {isDevs && (
        <div className="scheduleDetails">
          <h4>SequentialClass: {schedule?.sequentialScheduleId}</h4>
        </div>
      )}

      <CancelDialog
        classes={nextClasses}
        allSeqClasses={schedule}
        cancelSubmit={cancelSubmit}
        openCancelAlert={cancelAlertOpen}
        closeCancelAlert={closeCancelAlert}
        recurrentSchedule={recurrentSchedule}
        recurrentScheduleId={recurrentScheduleId}
        recurrentScheduleEndDate={recurrentScheduleEndDate}
        sequentialScheduleCancelClasses={sequentialScheduleCancelClasses}
      />

      <ModalSchedule
        editClass={true}
        userClass={schedule}
        title={t("EDIT_CLASS")}
        openSchedule={openSchedule}
        studentId={schedule?.studentId}
        planId={schedule?.studentPlanId}
        closeSchedule={handleCloseSchedule}
        noShow={schedule?.status === "noShow"}
        sequentialScheduleId={schedule?.sequentialScheduleId}
      />
    </div>
  );
}

export default ListCalendarSchedule;
